/**
 * Admin → Country coverage tab.
 *
 * Single-glance overview of every country currently supported by the app,
 * with per-country sync buttons so the admin no longer has to remember
 * URL slugs and run curl by hand.
 *
 * Data source: GET /api/admin/coverage — see server.py::admin_coverage.
 * Sync targets: the `sync_endpoint` returned per row (ED-269 country
 * specific endpoint for the 9 imports we own, or the generic
 * /api/admin/openaip/{cc}/sync for OpenAIP-fed countries).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { RotateCw, MapPin, AlertCircle, CheckCircle2, CircleSlash, History } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_BADGE = {
  'ED-269':  { label: 'ED-269 vector', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  'OpenAIP': { label: 'OpenAIP live',  cls: 'bg-sky-50 text-sky-700 border-sky-200' },
};

const STATUS_BADGE = {
  ok:      { label: 'OK',       Icon: CheckCircle2, cls: 'text-emerald-600' },
  stale:   { label: 'Stale',    Icon: AlertCircle,  cls: 'text-amber-600'   },
  missing: { label: 'Aucune',   Icon: CircleSlash,  cls: 'text-rose-600'    },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const authHeaders = () => {
  const token = localStorage.getItem('dronemaps_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export function AdminCoverageTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState({}); // { [cc]: true }

  const fetchCoverage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/coverage`, {
        headers: authHeaders(),
      });
      setRows(data.countries || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCoverage(); }, [fetchCoverage]);

  const onSync = useCallback(async (row) => {
    if (!row.sync_endpoint) return;
    const cc = row.country_code;
    setSyncing((s) => ({ ...s, [cc]: true }));
    try {
      const { data, status } = await axios.post(
        `${API_URL}${row.sync_endpoint}`,
        {},
        { headers: authHeaders(), timeout: 60000 },
      );

      // Async job path: backend returned 202 + { status: "running" } and exposes
      // a companion `${sync_endpoint}/status` endpoint we can poll. Used for
      // long-running syncs (Spain ENAIRE ~16k zones) that would otherwise hit
      // proxy/CDN timeouts on the front request.
      if (status === 202 || data?.status === 'running') {
        toast.info(`${row.flag} ${row.name} : sync lancée, patientez…`);
        const statusUrl = `${API_URL}${row.sync_endpoint}/status`;
        const started = Date.now();
        // DIPUL (Germany) processes a 370 MB ZIP + 1.2 GB JSON with ~88k
        // features on a shared production pod — 5-10 min is normal there
        // even though preview finishes in ~1 min. Give heavy syncs 20 min.
        const isHeavySync = row.country_code === 'de' || row.country_code === 'es';
        const MAX_MS = (isHeavySync ? 20 : 5) * 60 * 1000;
        // Show live progress ~every 10s so the user knows something is
        // happening. Using a ref-like local so we don't spam toasts.
        let lastProgressTs = 0;
        // Poll every 2s until completion
        while (true) {
          await new Promise((r) => setTimeout(r, 2000));
          if (Date.now() - started > MAX_MS) {
            throw new Error(`Sync trop longue (>${MAX_MS / 60000} min), abandon du sondage — vérifiez à nouveau dans quelques minutes, la tâche continue en arrière-plan`);
          }
          const { data: st } = await axios.get(statusUrl, {
            headers: authHeaders(), timeout: 30000,
          });
          if (st?.status === 'done') {
            const count = st.imported;
            toast.success(
              `${row.flag} ${row.name} : synchronisé ${count ? `(${count.toLocaleString('fr-FR')} zones, ${st.elapsed_seconds}s)` : ''}`,
            );
            break;
          }
          if (st?.status === 'error') {
            throw new Error(st.error || 'Sync échouée');
          }
          // Live progress toast every 10s for long syncs
          const now = Date.now();
          if (isHeavySync && now - lastProgressTs > 10000 && (st?.parsed || st?.imported)) {
            lastProgressTs = now;
            const elapsed = Math.round((now - started) / 1000);
            const detail = st.imported
              ? `${st.imported.toLocaleString('fr-FR')} zones importées`
              : st.downloaded
              ? `${(st.downloaded / 1024 / 1024).toFixed(0)} Mo téléchargés`
              : 'en cours';
            toast.info(`${row.flag} ${row.name} : ${detail} (${elapsed}s)`, { duration: 3000 });
          }
        }
      } else {
        const count = data?.total_kept ?? data?.imported ?? data?.count ?? data?.zones;
        toast.success(
          `${row.flag} ${row.name} : synchronisé ${count ? `(${count.toLocaleString('fr-FR')} zones)` : ''}`,
        );
      }

      // Refetch the whole table so timestamps & live counts update.
      fetchCoverage();
    } catch (err) {
      toast.error(
        `${row.flag} ${row.name} : ${err?.response?.data?.detail || err.message}`,
      );
    } finally {
      setSyncing((s) => ({ ...s, [cc]: false }));
    }
  }, [fetchCoverage]);

  const totals = useMemo(() => {
    let zones = 0, ok = 0, stale = 0, missing = 0;
    for (const r of rows) {
      zones += r.zones_in_db || 0;
      if (r.status === 'ok') ok++;
      else if (r.status === 'stale') stale++;
      else if (r.status === 'missing') missing++;
    }
    return { zones, ok, stale, missing };
  }, [rows]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="admin-coverage-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-rose-700" data-testid="admin-coverage-error">
        Erreur : {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-coverage-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Pays couverts"   value={rows.length}     tint="sky"     icon={MapPin} />
        <SummaryCard label="Zones en base"   value={totals.zones.toLocaleString('fr-FR')} tint="emerald" />
        <SummaryCard label="Synchronisation OK" value={totals.ok}      tint="emerald" icon={CheckCircle2} />
        <SummaryCard label="À vérifier"      value={totals.stale + totals.missing}    tint="amber" icon={AlertCircle} />
      </div>

      {/* Coverage table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-coverage-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium">Pays</th>
                <th className="px-4 py-3 font-medium">Tier</th>
                <th className="px-4 py-3 font-medium text-right">Zones BD</th>
                <th className="px-4 py-3 font-medium text-right">Live</th>
                <th className="px-4 py-3 font-medium">Dernier sync</th>
                <th className="px-4 py-3 font-medium">Statut</th>
                <th className="px-4 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const tier = TIER_BADGE[r.tier] || TIER_BADGE['ED-269'];
                const status = STATUS_BADGE[r.status] || STATUS_BADGE.ok;
                const StatusIcon = status.Icon;
                const isSyncing = !!syncing[r.country_code];
                return (
                  <tr
                    key={r.country_code}
                    className="border-t border-slate-100 hover:bg-slate-50/60"
                    data-testid={`admin-coverage-row-${r.country_code}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xl leading-none">{r.flag}</span>
                        <div>
                          <div className="font-medium text-slate-900">{r.name}</div>
                          <div className="text-xs text-slate-400 font-mono uppercase">{r.country_code}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs px-2 py-0.5 rounded border ${tier.cls}`}>
                        {tier.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700">
                      {r.zones_in_db > 0 ? r.zones_in_db.toLocaleString('fr-FR') : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700">
                      {r.live_count != null ? r.live_count.toLocaleString('fr-FR') : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {fmtDate(r.last_imported_at)}
                      {r.days_since_sync != null && (
                        <span className="ml-2 text-xs text-slate-400">(J-{r.days_since_sync})</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium ${status.cls}`}>
                        <StatusIcon className="w-3.5 h-3.5" />
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.sync_endpoint ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onSync(r)}
                          disabled={isSyncing}
                          data-testid={`admin-coverage-sync-${r.country_code}`}
                        >
                          <RotateCw className={`w-3.5 h-3.5 mr-1.5 ${isSyncing ? 'animate-spin' : ''}`} />
                          {isSyncing ? 'Sync…' : 'Sync'}
                        </Button>
                      ) : (
                        <span className="text-xs text-slate-400" title="Upload ED-269 manuel">manuel</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-slate-500">
        Les pays <span className="font-medium">ED-269 vector</span> stockent les zones en base de données après import depuis l&apos;autorité aéronautique nationale. Les pays <span className="font-medium">OpenAIP live</span> sont récupérés à la volée avec un cache de 6h ; le bouton « Sync » force le rafraîchissement du cache.
      </p>

      <UploadsHistory />
    </div>
  );
}


/**
 * Historique des imports ED-269 — audit trail listing every manual
 * upload with delta vs previous run. Backend endpoint:
 *   GET /api/admin/ed269/uploads
 * Rendered as a compact scrollable table so it can live below the
 * coverage grid without pushing the sync buttons off-screen.
 */
function UploadsHistory() {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/admin/ed269/uploads`, {
          params: { limit: 30 },
          headers: { Authorization: `Bearer ${localStorage.getItem('dronemaps_token')}` },
        });
        if (alive) setUploads(data?.uploads || []);
      } catch {
        // Section fails silently — never block the coverage tab.
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (uploads.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 p-4 bg-slate-50 text-sm text-slate-500">
        <div className="flex items-center gap-2 font-medium text-slate-700 mb-1">
          <History className="w-4 h-4" /> Historique des imports ED-269
        </div>
        Aucun import enregistré pour l&apos;instant.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="ed269-uploads-history">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
        <History className="w-4 h-4 text-slate-500" />
        <h3 className="text-sm font-semibold text-slate-800">Historique des imports ED-269</h3>
        <span className="text-xs text-slate-500 ml-1">— {uploads.length} dernier(s)</span>
      </div>
      <div className="max-h-80 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200 sticky top-0">
            <tr>
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Pays</th>
              <th className="px-4 py-2 text-left">Fichier</th>
              <th className="px-4 py-2 text-right">Importées</th>
              <th className="px-4 py-2 text-right">Total pays</th>
              <th className="px-4 py-2 text-right">Δ</th>
              <th className="px-4 py-2 text-left">Admin</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {uploads.map((u) => {
              const delta = u.delta;
              const deltaCls =
                delta === null || delta === undefined ? 'text-slate-400'
                  : delta > 0 ? 'text-emerald-600'
                  : delta < 0 ? 'text-rose-600'
                  : 'text-slate-500';
              const deltaStr =
                delta === null || delta === undefined ? '—'
                  : (delta > 0 ? `+${delta}` : String(delta));
              return (
                <tr key={u.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 whitespace-nowrap text-slate-700">
                    {new Date(u.imported_at).toLocaleString('fr-FR', {
                      day: '2-digit', month: '2-digit', year: '2-digit',
                      hour: '2-digit', minute: '2-digit',
                    })}
                  </td>
                  <td className="px-4 py-2 font-mono uppercase text-slate-800">{u.country}</td>
                  <td className="px-4 py-2 text-slate-600 truncate max-w-[220px]" title={u.filename}>
                    {u.filename}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                    {u.imported?.toLocaleString('fr-FR')}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold text-slate-900">
                    {u.country_total?.toLocaleString('fr-FR')}
                  </td>
                  <td className={`px-4 py-2 text-right tabular-nums font-semibold ${deltaCls}`}>
                    {deltaStr}
                  </td>
                  <td className="px-4 py-2 text-slate-500 text-xs">{u.imported_by || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TINT_CLS = {
  sky:     'bg-sky-50 text-sky-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  amber:   'bg-amber-50 text-amber-700',
};

const SummaryCard = ({ label, value, tint = 'sky', icon: Icon }) => (
  <div className={`rounded-xl border border-slate-200 p-4 ${TINT_CLS[tint] || TINT_CLS.sky}`}>
    <div className="flex items-center justify-between mb-1 text-xs uppercase tracking-wide font-medium opacity-80">
      <span>{label}</span>
      {Icon && <Icon className="w-4 h-4" />}
    </div>
    <div className="text-2xl font-semibold">{value}</div>
  </div>
);
